/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable prefer-const */

import { IM, useLanguage, useTheme } from '@infominds/react-native-components'
import { useAuthentication } from '@infominds/react-native-license'
import { useNavigation } from '@react-navigation/native'
import Moment from 'moment'
import React, { useMemo, useState } from 'react'
import { PixelRatio, Platform, RefreshControl, StyleSheet } from 'react-native'
import Spinner from 'react-native-modal-spinner'
import { SwipeListView } from 'react-native-swipe-list-view'

import api from '../apis/Api'
import Colors from '../constants/Colors'
import Layout from '../constants/Layout'
import useSettings from '../hooks/useSettings'
import { ArchiveScreenNavigationProp, Bill, HomeScreenNavigationProp } from '../types'
import { GroupOptions } from './BillListView'
import ListItem from './ListItem'
import { Text, TouchableOpacity, View } from './Themed'

interface Props {
  isOthers: boolean
  hasRightSwipe: boolean
  isArchive: boolean
  data: Bill[]
  searchText: string
  groupBy: GroupOptions
  refresh: () => void
  selectedBill?: Bill
  onSelectedBillChanged: (val: Bill) => void
  refreshing: boolean
}

const lowDPI = PixelRatio.get() < 2

function BillsList({
  isOthers,
  hasRightSwipe,
  isArchive,
  data: bills,
  searchText,
  groupBy,
  selectedBill,
  refreshing,
  refresh,
  onSelectedBillChanged,
}: Props) {
  const homeStackNavigation = useNavigation<HomeScreenNavigationProp>()
  const archiveStackNavigation = useNavigation<ArchiveScreenNavigationProp>()

  const { i18n } = useLanguage()
  const { colorScheme } = useTheme()
  const { historyDays } = useSettings()
  const { url, token, company } = useAuthentication()

  const [loading, setLoading] = useState(false)

  const groupedBills = useMemo(() => {
    // filer bills by search term
    if (searchText !== '') {
      bills = bills.filter(
        item => item.supplierName?.toLowerCase().includes(searchText.toLowerCase()) || item.billID.toLowerCase().includes(searchText.toLowerCase())
      )
    }

    // group bills by property
    // @ts-ignore todo
    const groupByKey = (list: Bill[], key: string) => list.reduce((hash, obj) => ({ ...hash, [obj[key]]: (hash[obj[key]] || []).concat(obj) }), {})

    const data = []

    if (bills) {
      let sorted = []
      if (groupBy === 'billDate') {
        sorted = bills.sort((a, b) => new Date(b.billDate).getTime() - new Date(a.billDate).getTime())
      } else if (groupBy === 'firstDueDate') {
        sorted = bills.sort((a, b) => new Date(b.firstDueDate ?? 0).getTime() - new Date(a.firstDueDate ?? 0).getTime())
      } else {
        sorted = bills.sort((a, b) => (a.supplierName ?? '').localeCompare(b.supplierName ?? ''))
      }

      const tmp = groupByKey(sorted, groupBy)

      for (let [key, value] of Object.entries(tmp)) {
        if (groupBy === 'billDate') {
          key = i18n.t('ID_BILLDATE') + ': ' + Moment(key).format('DD/MM/yyyy')
        } else if (groupBy === 'firstDueDate') {
          key = i18n.t('ID_DUEDATE') + ': ' + Moment(key).format('DD/MM/yyyy')
        }

        data.push({ title: key, data: value as Bill[] })
      }
    }

    return data
  }, [searchText, groupBy, bills])

  const approve = (item: Bill, accept: boolean) => {
    setLoading(true)

    api
      .approve(token, url, company, item.msgId, item.billID, item.supplierID, item.billDate, accept, '')
      .then(() => {
        refresh()
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  const renderItem = ({ item }: { item: Bill }) => {
    return (
      <ListItem
        item={item}
        isSelected={!Layout.isSmallDevice && item.billID === selectedBill?.billID}
        isOthers={isOthers}
        isArchive={isArchive}
        onPress={() => {
          onSelectedBillChanged(item)
          if (!Layout.isSmallDevice) onSelectedBillChanged(item)
        }}
        onStatusPress={() => {
          if (isArchive) {
            archiveStackNavigation.navigate('HistoryScreen', { bill: item })
          } else {
            homeStackNavigation.navigate('HistoryScreen', { bill: item })
          }
        }}
      />
    )
  }

  const renderSectionHeader = ({ section: { title } }: any) => {
    return (
      <View>
        <Text style={styles.listHeader}>{title}</Text>
      </View>
    )
  }

  const renderHiddenItem = ({ item }: { item: Bill }) => (
    <View style={styles.rowBack}>
      <TouchableOpacity
        style={styles.backLeftBtn}
        onPress={() => {
          if (isArchive) {
            archiveStackNavigation.navigate('PreviewScreen', { bill: item })
          } else {
            homeStackNavigation.navigate('PreviewScreen', { bill: item })
          }
        }}>
        <IM.Icon size={28} color="#FFFFFF" icon={['far', 'file']} />
      </TouchableOpacity>
      {hasRightSwipe && (
        <React.Fragment>
          <TouchableOpacity style={[styles.backRightBtn, styles.backRightBtnLeft]} onPress={() => approve(item, true)}>
            <IM.Icon size={28} color="#FFFFFF" icon={['far', 'thumbs-up']} />
          </TouchableOpacity>
          <TouchableOpacity style={[styles.backRightBtn, styles.backRightBtnRight]} onPress={() => approve(item, false)}>
            <IM.Icon size={28} color="#FFFFFF" icon={['far', 'thumbs-down']} />
          </TouchableOpacity>
        </React.Fragment>
      )}
    </View>
  )

  const listEmptyComponent = () => {
    if (groupedBills.length !== 0 || refreshing) return <></>

    return (
      // eslint-disable-next-line react-native/no-inline-styles
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 10 }}>
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <Text style={{ color: colorScheme === 'dark' ? 'gray' : 'lightgrey', fontSize: 20, fontWeight: '500', paddingBottom: 6 }}>
          {isArchive ? i18n.t('NO_ELEMENT_FOUND') : i18n.t('NO_INVOICE_TITLE')}
        </Text>

        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <Text style={{ color: colorScheme === 'dark' ? 'gray' : 'lightgrey', fontSize: 16, textAlign: 'center' }}>
          {isArchive
            ? historyDays === 1
              ? i18n.t('HISTORY_SETTINGS_DETAILS_LAST_DAY')
              : i18n.t('HISTORY_SETTINGS_DETAILS_LAST_DAYS').replace('${value}', historyDays.toString())
            : i18n.t('NO_INVOICE')}
        </Text>
      </View>
    )
  }

  const rightOffset = hasRightSwipe ? -150 : 0

  return (
    <View style={styles.container} lightColor={Colors.light.background} darkColor={Colors.dark.background}>
      {Platform.OS !== 'web' ? <Spinner color={Colors.tint} size="large" backgroundColor="transparent" visible={loading} /> : <></>}
      <SwipeListView
        sections={groupedBills}
        useSectionList={true}
        renderItem={renderItem}
        keyExtractor={(item: Bill, index: number) => item.billID + index.toString()}
        refreshing={refreshing}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => refresh()} colors={[Colors.tint]} tintColor={Colors.tint} />}
        renderSectionHeader={renderSectionHeader}
        renderHiddenItem={renderHiddenItem}
        ListEmptyComponent={listEmptyComponent}
        leftOpenValue={75}
        rightOpenValue={rightOffset}
        // eslint-disable-next-line react-native/no-inline-styles
        contentContainerStyle={{ flex: groupedBills.length === 0 ? 1 : undefined }}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 2,
    justifyContent: 'space-between',
  },
  rowBack: {
    margin: 1,
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
  },
  backLeftBtn: {
    alignItems: 'center',
    bottom: lowDPI ? 1 : 0,
    justifyContent: 'center',
    position: 'absolute',
    backgroundColor: Colors.tint,
    width: 75,
    top: 0,
  },
  backRightBtn: {
    alignItems: 'center',
    bottom: lowDPI ? 1 : 0,
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    width: 75,
  },
  backRightBtnLeft: {
    backgroundColor: Colors.tint,
    right: 75,
  },
  backRightBtnRight: {
    backgroundColor: Colors.tintOpposite,
    right: 0,
  },
  listHeader: {
    margin: 10,
  },
})

export default BillsList
